import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AddUserModal = ({ 
  isOpen, 
  onClose, 
  onAddUser, 
  editMode = false, 
  initialData = null,
  title = "Add New User"
}) => {
  // Force dark mode for admin components
  useEffect(() => {
    document.documentElement?.setAttribute('data-theme', 'dark');
  }, []);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'artist',
    artist_type: '',
    bio: '',
    location: '',
    website: '',
    status: 'active'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  // Reset form when modal opens/closes or when switching between add/edit modes
  useEffect(() => {
    if (isOpen) {
      if (editMode && initialData) {
        setFormData({
          full_name: initialData?.full_name || '',
          email: initialData?.email || '',
          password: '', // Don't populate password for edit mode
          role: initialData?.role || 'artist',
          artist_type: initialData?.artist_type || '',
          bio: initialData?.bio || '',
          location: initialData?.location || '',
          website: initialData?.website || '',
          status: initialData?.status || 'active'
        });
      } else {
        // Reset to empty form for add mode
        setFormData({
          full_name: '',
          email: '',
          password: '',
          role: 'artist',
          artist_type: '',
          bio: '',
          location: '',
          website: '',
          status: 'active'
        });
      }
      setErrors({});
      setPasswordStrength(0);
    }
  }, [isOpen, editMode, initialData]);

  // Enhanced password strength checker
  const calculatePasswordStrength = (password) => {
    if (!password) return 0;
    
    let score = 0;
    if (password?.length >= 8) score += 1;
    if (password?.match(/[a-z]/)) score += 1;
    if (password?.match(/[A-Z]/)) score += 1;
    if (password?.match(/[0-9]/)) score += 1;
    if (password?.match(/[^a-zA-Z0-9]/)) score += 1;
    
    return score;
  };

  // Enhanced form validation
  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData?.full_name?.trim()) {
      newErrors.full_name = 'Full name is required';
    } else if (formData?.full_name?.trim()?.length < 2) {
      newErrors.full_name = 'Name must be at least 2 characters long';
    }

    // Email validation
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex?.test(formData?.email?.trim())) {
        newErrors.email = 'Please enter a valid email address (example@domain.com)';
      }
    }

    // Password validation (only for new users or when password is provided)
    if (!editMode || formData?.password) {
      if (!formData?.password) {
        if (!editMode) {
          newErrors.password = 'Password is required for new users';
        }
      } else {
        if (formData?.password?.length < 8) {
          newErrors.password = 'Password must be at least 8 characters long';
        } else if (passwordStrength < 3) {
          newErrors.password = 'Password is too weak. Include uppercase, lowercase, numbers, and special characters';
        }
      }
    }

    // Role validation
    if (!formData?.role) {
      newErrors.role = 'User role is required';
    }

    // Artist type validation (only for artists)
    if (formData?.role === 'artist' && !formData?.artist_type) {
      newErrors.artist_type = 'Artist type is required for artist accounts';
    }

    // Website validation (if provided)
    if (formData?.website && formData?.website?.trim()) {
      const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      if (!urlRegex?.test(formData?.website?.trim())) {
        newErrors.website = 'Please enter a valid website URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Real-time password strength calculation
    if (field === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
    
    // Clear specific field error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
  };

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 0:
      case 1: return { text: 'Very Weak', color: 'text-red-600' };
      case 2: return { text: 'Weak', color: 'text-orange-600' };
      case 3: return { text: 'Good', color: 'text-yellow-600' };
      case 4: return { text: 'Strong', color: 'text-green-600' };
      case 5: return { text: 'Very Strong', color: 'text-green-700' };
      default: return { text: '', color: '' };
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({}); // Clear previous errors
    
    try {
      console.log('Submitting user creation request...');
      
      // Prepare user data with validation
      const userData = {
        full_name: formData?.full_name?.trim(),
        email: formData?.email?.trim()?.toLowerCase(),
        role: formData?.role,
        bio: formData?.bio?.trim() || '',
        location: formData?.location?.trim() || '',
        website: formData?.website?.trim() || '',
        status: formData?.status || 'active'
      };

      // Add password only if provided (for new users) or when editing and password is set
      if (formData?.password) {
        userData.password = formData?.password;
      }

      // Add artist type only for artists
      if (formData?.role === 'artist' && formData?.artist_type) {
        userData.artist_type = formData?.artist_type;
      }

      console.log('User data prepared:', { ...userData, password: userData?.password ? '[HIDDEN]' : undefined });

      await onAddUser(userData);
      
      console.log('User creation successful!');
      
      // Reset form only on successful creation
      setFormData({
        full_name: '',
        email: '',
        password: '',
        role: 'artist',
        artist_type: '',
        bio: '',
        location: '',
        website: '',
        status: 'active'
      });
      setPasswordStrength(0);
      
      onClose();
    } catch (error) {
      console.error('User creation error:', error);
      
      // Enhanced error categorization
      let errorMessage = 'Failed to create user. Please try again.';
      
      if (error?.message?.includes('already exists') || error?.message?.includes('duplicate')) {
        errorMessage = 'A user with this email already exists. Please use a different email address.';
      } else if (error?.message?.includes('invalid email') || error?.message?.includes('malformed')) {
        errorMessage = 'Please provide a valid email address in the correct format.';
      } else if (error?.message?.includes('weak password') || error?.message?.includes('password')) {
        errorMessage = 'Password does not meet security requirements. Please use a stronger password.';
      } else if (error?.message?.includes('service role') || error?.message?.includes('not properly configured')) {
        errorMessage = 'User creation is not properly configured. Please contact your system administrator to set up the service role permissions.';
      } else if (error?.message?.includes('permission') || error?.message?.includes('unauthorized')) {
        errorMessage = 'You do not have permission to create users. Please ensure you are logged in as an administrator.';
      } else if (error?.message?.includes('Authentication required') || error?.message?.includes('session expired')) {
        errorMessage = 'Authentication session expired. Please refresh the page and log in again as an administrator.';
      } else if (error?.message?.includes('network') || error?.message?.includes('connection')) {
        errorMessage = 'Network connection error. Please check your internet connection and try again.';
      } else if (error?.message) {
        errorMessage = error?.message;
      }
      
      setErrors({ 
        submit: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      full_name: '',
      email: '',
      password: '',
      role: 'artist',
      artist_type: '',
      bio: '',
      location: '',
      website: '',
      status: 'active'
    });
    setErrors({});
    setPasswordStrength(0);
    setShowPassword(false);
    onClose();
  };

  if (!isOpen) return null;

  const strengthInfo = getPasswordStrengthText();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                {title || (editMode ? 'Edit User' : 'Add New User')}
              </h2>
              <p className="text-sm text-text-secondary mt-1">
                {editMode ? 'Update user information and permissions' : 'Create a new user account with appropriate role and permissions'}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              disabled={isSubmitting}
            >
              <Icon name="X" size={20} className="text-text-secondary" />
            </button>
          </div>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-text-primary border-b border-border pb-2">
              Basic Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  type="text"
                  label="Full Name"
                  value={formData?.full_name}
                  onChange={(e) => handleInputChange('full_name', e?.target?.value)}
                  placeholder="Enter full name"
                  error={errors?.full_name}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <Input
                  type="email"
                  label="Email Address"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  placeholder="user@example.com"
                  error={errors?.email}
                  required
                  disabled={editMode || isSubmitting} // Don't allow email changes in edit mode
                />
                {editMode && (
                  <p className="text-xs text-text-secondary mt-1">
                    Email cannot be changed after account creation
                  </p>
                )}
              </div>
            </div>

            {/* Password field with strength indicator */}
            {!editMode && (
              <div>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    label="Password"
                    value={formData?.password}
                    onChange={(e) => handleInputChange('password', e?.target?.value)}
                    placeholder="Enter secure password"
                    error={errors?.password}
                    required={!editMode}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-8 p-1 hover:bg-muted rounded transition-colors"
                    disabled={isSubmitting}
                  >
                    <Icon 
                      name={showPassword ? "EyeOff" : "Eye"} 
                      size={16} 
                      className="text-text-secondary" 
                    />
                  </button>
                </div>
                
                {/* Password strength indicator */}
                {formData?.password && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex gap-1 flex-1">
                        {[1, 2, 3, 4, 5]?.map((level) => (
                          <div
                            key={level}
                            className={`h-1 flex-1 rounded-full ${
                              passwordStrength >= level
                                ? passwordStrength <= 2
                                  ? 'bg-red-500'
                                  : passwordStrength === 3
                                  ? 'bg-yellow-500' :'bg-green-500' :'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                      <span className={`text-xs font-medium ${strengthInfo?.color}`}>
                        {strengthInfo?.text}
                      </span>
                    </div>
                    <div className="text-xs text-text-secondary">
                      Password must include: uppercase, lowercase, numbers, and special characters
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Role and Permissions */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-text-primary border-b border-border pb-2">
              Role & Permissions
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Select
                  label="User Role"
                  options={[
                    { value: 'admin', label: '🔧 Admin - Full platform access' },
                    { value: 'editor', label: '✏️ Editor - Content management' },
                    { value: 'artist', label: '🎨 Artist - Profile & content creation' }
                  ]}
                  value={formData?.role}
                  onChange={(value) => handleInputChange('role', value)}
                  error={errors?.role}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {formData?.role === 'artist' && (
                <div>
                  <Select
                    label="Artist Type"
                    options={[
                      { value: 'musician', label: '🎵 Musician' },
                      { value: 'visual', label: '🎨 Visual Artist' },
                      { value: 'director', label: '🎬 Director' },
                      { value: 'actor', label: '🎭 Actor' },
                      { value: 'writer', label: '✍️ Writer' }
                    ]}
                    value={formData?.artist_type}
                    onChange={(value) => handleInputChange('artist_type', value)}
                    placeholder="Select artist specialization"
                    error={errors?.artist_type}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              )}

              {editMode && (
                <div>
                  <Select
                    label="Account Status"
                    options={[
                      { value: 'active', label: '✅ Active' },
                      { value: 'inactive', label: '⏸️ Inactive' },
                      { value: 'pending', label: '⏳ Pending Verification' },
                      { value: 'suspended', label: '🚫 Suspended' }
                    ]}
                    value={formData?.status}
                    onChange={(value) => handleInputChange('status', value)}
                    error={errors?.status}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-text-primary border-b border-border pb-2">
              Additional Information
            </h3>
            
            <div className="space-y-4">
              <div>
                <Input
                  type="text"
                  label="Bio / Description"
                  value={formData?.bio}
                  onChange={(e) => handleInputChange('bio', e?.target?.value)}
                  placeholder="Brief description about the user"
                  multiline
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Input
                    type="text"
                    label="Location"
                    value={formData?.location}
                    onChange={(e) => handleInputChange('location', e?.target?.value)}
                    placeholder="City, State/Country"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <Input
                    type="url"
                    label="Website"
                    value={formData?.website}
                    onChange={(e) => handleInputChange('website', e?.target?.value)}
                    placeholder="https://example.com"
                    error={errors?.website}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {errors?.submit && (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={16} className="text-destructive mt-0.5 flex-shrink-0" />
                <div className="w-full">
                  <p className="text-sm text-destructive font-medium">User Creation Failed</p>
                  <div className="text-sm text-destructive/90 mt-1 whitespace-pre-line">
                    {errors?.submit}
                  </div>
                  
                  {/* Configuration Help */}
                  {(errors?.submit?.includes('service role') || 
                    errors?.submit?.includes('configuration') ||
                    errors?.submit?.includes('not properly configured')) && (
                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-xs">
                      <p className="font-medium text-amber-800 mb-2">🔧 Configuration Required</p>
                      <div className="space-y-2 text-amber-700">
                        <p><strong>For System Administrators:</strong></p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Add <code className="bg-amber-100 px-1 rounded">VITE_SUPABASE_SERVICE_ROLE_KEY</code> to environment variables</li>
                          <li>Enable "User Management" in Supabase Dashboard → Authentication → Settings</li>
                          <li>Configure RLS policies to allow admin user creation</li>
                          <li>Verify project billing status (admin features may require paid plan)</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  <button 
                    onClick={() => setErrors({})}
                    className="mt-3 text-destructive hover:text-destructive/80 underline text-sm font-medium"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Info Panel */}
          <div className="bg-muted/50 p-4 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={16} className="text-accent mt-0.5 flex-shrink-0" />
              <div className="text-sm text-text-secondary">
                <p className="font-medium text-text-primary mb-2">
                  {editMode ? 'Account Update Process:' : 'Account Creation Process:'}
                </p>
                <ul className="space-y-1 text-xs">
                  {editMode ? (
                    <>
                      <li>• Profile information will be updated immediately</li>
                      <li>• User will be notified of any role changes</li>
                      <li>• Status changes take effect immediately</li>
                    </>
                  ) : (
                    <>
                      <li>• A secure account will be created in the authentication system</li>
                      <li>• User profile will be set up with the specified role and information</li>
                      <li>• An email with login instructions will be sent to the user</li>
                      <li>• Account status will be set to "Active" once email is verified</li>
                      <li>• User can change their password after first login</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
              iconName={editMode ? "Save" : "UserPlus"}
              iconPosition="left"
            >
              {editMode ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;